import React, { useState, useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  DivergenciasUnificadaResponseDto,
  TipoDivergenciaEnum,
  PaginacaoDto
} from '@/services/conciliacao-fiscal';
import DivergenciasSummaryCards from './DivergenciasSummaryCards';

interface DivergenciasUnificadasTableProps {
  data: DivergenciasUnificadaResponseDto;
  paginacao: PaginacaoDto;
  loading?: boolean;
  error?: string | null;
  onPageChange: (page: number) => void;
  onSearch?: (query: string) => void;
  onExport?: () => void;
}

// Color scheme for type differentiation with accessibility compliance (WCAG AA 4.5:1)
const getTipoConfig = (tipo: TipoDivergenciaEnum) => {
  const configs = {
    [TipoDivergenciaEnum.PULO_NUMERACAO]: {
      bg: 'hover:bg-blue-50',
      badge: 'bg-blue-100 text-blue-800 border-blue-300',
      icon: '🔄',
      label: 'Pulo',
      ariaLabel: 'Tipo: Pulo de numeração'
    },
    [TipoDivergenciaEnum.EXCLUSIVO_DOC001]: {
      bg: 'hover:bg-orange-50',
      badge: 'bg-orange-100 text-orange-800 border-orange-300',
      icon: '📄',
      label: 'DOC001',
      ariaLabel: 'Tipo: Exclusivo DOC001'
    },
    [TipoDivergenciaEnum.EXCLUSIVO_VALIDATOR]: {
      bg: 'hover:bg-purple-50',
      badge: 'bg-purple-100 text-purple-800 border-purple-300',
      icon: '✓',
      label: 'Validator',
      ariaLabel: 'Tipo: Exclusivo Validator'
    },
    [TipoDivergenciaEnum.STATUS_DIVERGENTE]: {
      bg: 'hover:bg-red-50',
      badge: 'bg-red-100 text-red-800 border-red-300',
      icon: '⚠️',
      label: 'Status',
      ariaLabel: 'Tipo: Status divergente'
    },
    // GFT Livro Fiscal types (WCAG AA compliant)
    [TipoDivergenciaEnum.PULO_NUMERACAO_GFT]: {
      bg: 'hover:bg-cyan-50',
      badge: 'bg-cyan-600 text-white border-cyan-700',
      icon: '📈',
      label: 'Pulo GFT',
      ariaLabel: 'Tipo: Pulo de numeração GFT'
    },
    [TipoDivergenciaEnum.STATUS_DIVERGENTE_GFT]: {
      bg: 'hover:bg-cyan-50',
      badge: 'bg-cyan-600 text-white border-cyan-700',
      icon: '🔄',
      label: 'DOC↔GFT',
      ariaLabel: 'Tipo: Status divergente entre DOC001 e GFT'
    },
    [TipoDivergenciaEnum.EXCLUSIVO_GFT_LIVRO]: {
      bg: 'hover:bg-lime-50',
      badge: 'bg-lime-500 text-black border-lime-600',
      icon: '📗',
      label: 'Exclusivo GFT',
      ariaLabel: 'Tipo: Exclusivo GFT Livro Fiscal'
    },
    [TipoDivergenciaEnum.EXCLUSIVO_DOC001_GFT]: {
      bg: 'hover:bg-sky-50',
      badge: 'bg-sky-600 text-white border-sky-700',
      icon: '📘',
      label: 'Exclusivo VAR',
      ariaLabel: 'Tipo: Exclusivo DOC001 vs GFT'
    },
    [TipoDivergenciaEnum.STATUS_DIVERGENTE_GFT_VALIDATOR]: {
      bg: 'hover:bg-indigo-50',
      badge: 'bg-indigo-600 text-white border-indigo-700',
      icon: '🔀',
      label: 'GFT↔Migrate',
      ariaLabel: 'Tipo: Status divergente entre GFT e Migrate'
    }
  };
  return configs[tipo];
};

export default function DivergenciasUnificadasTable({
  data,
  paginacao,
  loading = false,
  error = null,
  onPageChange,
  onExport
}: DivergenciasUnificadasTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [searchQuery] = useState('');
  const [announcements, setAnnouncements] = useState<string>('');

  const { divergencias, resumoPorTipo } = data;

  // Handle row expansion with accessibility announcements
  const handleRowExpand = useCallback((divergenciaId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      const isExpanded = newSet.has(divergenciaId);

      if (isExpanded) {
        newSet.delete(divergenciaId);
        setAnnouncements('Detalhes da divergência recolhidos');
      } else {
        newSet.add(divergenciaId);
        setAnnouncements('Detalhes da divergência expandidos');
      }

      // Clear announcement after it's been read
      setTimeout(() => setAnnouncements(''), 1000);

      return newSet;
    });
  }, []);


  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent, divergenciaId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRowExpand(divergenciaId);
    }
  }, [handleRowExpand]);

  // Filtered data based on search query
  const filteredDivergencias = useMemo(() => {
    if (!searchQuery.trim()) return divergencias;

    const query = searchQuery.toLowerCase();
    return divergencias.filter(divergencia =>
      divergencia.loja.toLowerCase().includes(query) ||
      divergencia.caixa.toLowerCase().includes(query) ||
      divergencia.cupom.toString().includes(query) ||
      getTipoConfig(divergencia.tipo).label.toLowerCase().includes(query)
    );
  }, [divergencias, searchQuery]);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          {/* Loading skeleton */}
          <div className="h-8 bg-gray-200 rounded animate-pulse" />
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">Erro ao carregar dados: {error}</p>
          <Button onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </div>
      </Card>
    );
  }

  if (divergencias.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold mb-2">Nenhuma divergência encontrada</h3>
          <p className="text-muted-foreground">
            {searchQuery
              ? `Nenhum resultado para "${searchQuery}". Tente termos diferentes.`
              : 'Nenhuma divergência encontrada para este período.'
            }
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Screen reader announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcements}
      </div>

      <div className="space-y-6">
        {/* Summary Cards */}
        <DivergenciasSummaryCards resumoPorTipo={resumoPorTipo} />

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              aria-label="Exportar divergências"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>

        {/* Main Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table role="table" aria-label="Lista unificada de divergências fiscais">
            <TableHeader>
              <TableRow role="row">
                <TableHead role="columnheader" scope="col">Tipo</TableHead>
                <TableHead role="columnheader" scope="col">Loja</TableHead>
                <TableHead role="columnheader" scope="col">Caixa</TableHead>
                <TableHead role="columnheader" scope="col">Cupom</TableHead>
                <TableHead role="columnheader" scope="col">Migrate</TableHead>
                <TableHead role="columnheader" scope="col">VAR</TableHead>
                <TableHead role="columnheader" scope="col">GFT</TableHead>
                <TableHead role="columnheader" scope="col">Logix</TableHead>
                <TableHead role="columnheader" scope="col">Data</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody role="rowgroup">
              {filteredDivergencias.map((divergencia) => {
                const tipoConfig = getTipoConfig(divergencia.tipo);
                const isExpanded = expandedRows.has(divergencia.id);

                return (
                  <React.Fragment key={divergencia.id}>
                    {/* Main row */}
                    <TableRow
                      className={`cursor-pointer transition-colors ${tipoConfig.bg} ${
                        isExpanded ? 'bg-slate-100' : ''
                      }`}
                      onClick={() => handleRowExpand(divergencia.id)}
                      onKeyDown={(e) => handleKeyDown(e, divergencia.id)}
                      tabIndex={0}
                      role="button"
                      aria-expanded={isExpanded}
                      aria-label={`${tipoConfig.ariaLabel}, ${isExpanded ? 'expandido' : 'recolhido'}`}
                    >
                      <TableCell>
                        <Badge
                          className={tipoConfig.badge}
                          aria-label={tipoConfig.ariaLabel}
                        >
                          <span className="mr-1" aria-hidden="true">{tipoConfig.icon}</span>
                          {tipoConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{divergencia.loja}</TableCell>
                      <TableCell>{divergencia.caixa}</TableCell>
                      <TableCell>{divergencia.cupom.toLocaleString()}</TableCell>
                      <TableCell>
                        {divergencia.tipo === TipoDivergenciaEnum.PULO_NUMERACAO
                          ? (divergencia.statusValidatorPulos || '-')
                          : (divergencia.statusValidator || '-')
                        }
                      </TableCell>
                      <TableCell>{divergencia.statusDoc001Calculado || divergencia.statusDoc001}</TableCell>
                      <TableCell>{divergencia.statusGftLivro}</TableCell>
                      <TableCell>{divergencia.inutilizadoLogix ? "Sim" : "Não"}</TableCell>
                      <TableCell>
                        {format(new Date(divergencia.data), 'dd/MM/yyyy', { locale: ptBR })}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {paginacao.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              Mostrando página {paginacao.page} de {paginacao.totalPages}
              ({paginacao.total} divergências total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(paginacao.page - 1)}
                disabled={paginacao.page === 1 || loading}
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(paginacao.page + 1)}
                disabled={paginacao.page === paginacao.totalPages || loading}
                aria-label="Próxima página"
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                Carregando...
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}